import {
	coalesceLineChartX,
	JEPTO_CARTESIAN_DATETIME_LABEL_FORMATS,
} from '../../../utils/highcharts-datetime.util';
import {
	buildTooltipComparisonSuffixHtml,
	chartTooltipHtmlTheme,
	resolveTooltipComparisonPoint,
} from '../../../utils/chart-comparison-tooltip.util';
import {
	buildCartesianAggregationLegendLabelHtml,
	chartHasComparisonLegendRows,
	resolveLegendSeriesUserOptions,
} from '../../../utils/chart-legend-scale.util';
import { resolveCssNumber } from '../../../utils/resolve-highcharts-css-colors.util';
import Highcharts from '../highcharts.config';

const formatNumber = (value: number) => {
	// add commas and make decimal optional, only show if it's not 0
	return value
		.toLocaleString('en-US', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})
		.replace(/\.00$/, '');
};

/** Shared tooltip header for categorical x-axis (avoids dateFormat on index ms). */
function lineChartCategoryTooltipHeader(ctx: any, points: any[]): string {
	const pt0 = points[0];
	if (pt0?.category != null && pt0.category !== '') {
		return String(pt0.category);
	}
	if (typeof ctx.x === 'string') {
		return ctx.x;
	}
	const categories = pt0?.series?.xAxis?.categories;
	if (Array.isArray(categories) && typeof ctx.x === 'number') {
		const c = categories[ctx.x];
		if (c != null) {
			return String(c);
		}
	}
	if (ctx.key != null && ctx.key !== '') {
		return String(ctx.key);
	}
	return ctx.x != null ? String(ctx.x) : '';
}

export function createLineChartTooltipFormatter(
	xAxisType: 'datetime' | 'category',
	pinnedThemeMode?: 'light' | 'dark'
): Highcharts.TooltipFormatterCallbackFunction {
	const theme = pinnedThemeMode
		? chartTooltipHtmlTheme(pinnedThemeMode)
		: null;
	const textSecondary =
		theme?.textSecondary ?? 'var(--text-secondary-color)';
	const textPrimary = theme?.textPrimary ?? 'var(--text-primary-color)';
	const background =
		theme?.background ?? 'var(--background-primary-color)';

	return function (this: any) {
		const points = this.points || [];
		const date =
			xAxisType === 'datetime'
				? Highcharts.dateFormat('%b %d, %Y', this.x)
				: lineChartCategoryTooltipHeader(this, points);
		let tooltipContent = `<div style="font-size: var(--font-size-xs); font-weight: var(--font-weight-regular); color: ${textSecondary}; margin-bottom: 5px;">${date}</div>`;

		const getLineStyle = (point: any) => {
			return `background-color: ${point.series.color}`;
		};

		points.forEach(point => {
			const seriesOptions = point.series.userOptions || {};
			const comparison = resolveTooltipComparisonPoint(
				seriesOptions.comparisonData,
				point.index ?? 0
			);

			const customOptions =
				point.series.options.custom || seriesOptions.custom || {};
			const inverseMetric = customOptions.inverseMetric;

			const comparisonHtml =
				comparison !== undefined
					? buildTooltipComparisonSuffixHtml(
							point.y,
							comparison,
							formatNumber,
							inverseMetric
						)
					: '';

			tooltipContent += `
                <div style="display: flex; align-items: center; margin-bottom: 5px;">
                    <div style="width: 3px; height: 30px; ${getLineStyle(
						point
					)}; margin-right: 8px;"></div>
                    <div style="display: flex; flex-direction: column;">
                        <span style="color: ${textSecondary}; font-size: var(--font-size-xs); font-weight: var(--font-weight-regular);">${
							point.series.name
						}</span>
                        <div style="color: ${textPrimary}; font-weight: var(--font-weight-regular); font-size: var(--font-size-sm)">
                            ${formatNumber(point.y)}${comparisonHtml}
                        </div>
                    </div>
                </div>`;
		});

		return `<div style="padding: 5px; background: ${background};">${tooltipContent}</div>`;
	};
}

export const lineChartDefaults: Highcharts.Options = {
	title: {
		text: undefined,
	},
	subtitle: {
		text: undefined,
	},
	series: [],
	chart: {
		type: 'line',
		animation: true,
		backgroundColor: 'transparent',
		style: {
			fontFamily: 'var(--font-family)',
		},
		spacing: [10, 20, 10, 20],
	},
	xAxis: {
		type: 'datetime',
		labels: {
			style: {
				color: 'var(--text-secondary-color)',
			},
		},
		tickmarkPlacement: 'on',
		gridLineWidth: 1,
		gridLineColor: 'var(--border-secondary-color)',
		lineColor: 'var(--border-primary-color)',
		tickColor: 'var(--border-primary-color)',
		dateTimeLabelFormats: JEPTO_CARTESIAN_DATETIME_LABEL_FORMATS,
	},
	yAxis: {
		title: {
			text: undefined,
		},
		labels: {
			style: {
				color: 'var(--text-primary-color)',
			},
		},
		gridLineColor: 'var(--border-secondary-color)',
		min: 0,
	},
	plotOptions: {
		area: {
			marker: {
				enabled: false,
				states: {
					hover: { enabled: false },
				},
			},
			fillOpacity: 0.2,
		},
		line: {
			marker: {
				enabled: false,
			},
		},
		spline: {
			marker: {
				enabled: false,
			},
			states: {
				hover: {
					lineWidth: 2,
				},
			},
		},
		series: {
			lineWidth: 2,
			dashStyle: 'Solid',
			opacity: 1,
			zIndex: 1,
		},
	},
	legend: {
		useHTML: true,
		labelFormatter: function () {
			const seriesRef = this as any;
			const options = resolveLegendSeriesUserOptions(seriesRef);
			const lineStyle = `background-color: ${this.color}`;
			const chart = seriesRef.chart;

			return buildCartesianAggregationLegendLabelHtml({
				seriesName: this.name,
				swatchInlineStyle: lineStyle,
				aggregationValue: options.aggregationValue,
				comparisonAggregationValue: options.comparisonAggregationValue,
				comparisonData: options.comparisonData,
				formatNumber,
				comparisonRowEnabled: chartHasComparisonLegendRows(
					chart?.series
				),
			});
		},
		align: 'left',
		verticalAlign: 'top',
		itemStyle: {
			color: 'var(--text-primary-color)',
			fontWeight: 'normal',
			fontSize: 'var(--font-size-md)',
		},
		itemHoverStyle: {
			color: 'var(--text-primary-color)',
		},
		symbolRadius: 0,
		symbolWidth: 0,
		symbolHeight: 0,
		symbolPadding: 4,
		itemDistance: 12,
		layout: 'horizontal',
		alignColumns: true,
		itemMarginBottom: 2,
		navigation: {
			activeColor: 'var(--text-link-color)',
			inactiveColor: 'var(--text-secondary-color)',
			animation: true,
		},
	},
	tooltip: {
		outside: true,
		enabled: true,
		shared: true,
		useHTML: true,
		shadow: {
			color: 'var(--card-shadow-color)',
			opacity: 1,
			offsetX: 0,
			offsetY: 4,
			width: 12,
		},
		backgroundColor: 'var(--background-primary-color)',
		borderColor: 'var(--border-primary-color)',
		borderWidth: resolveCssNumber('--card-border-width', 1),
		borderRadius: resolveCssNumber('--card-border-radius', 6),
		formatter: createLineChartTooltipFormatter('datetime'),
		style: {
			color: 'var(--text-primary-color)',
			fontSize: 'var(--font-size-sm)',
		},
	},
	credits: {
		enabled: false,
	},
};
